// Module: slideCaptureCss
// -----------------------------------------------------------------
// Receives:  the live `.reveal` element and the page's stylesheets
// Produces:  the CSS a slide capture needs, and puts it into the clone
// Owns:      rule selection, font stylesheet narrowing, the clone's font wait
// Does NOT:  run html2canvas or decide when to capture (→ slideScreenshot)
// -----------------------------------------------------------------
//
// html2canvas copies every <head> stylesheet into its clone, and an iPad then
// parses ~930 KB of app CSS for a slide that uses ~100 rules: ~2s frozen.

// Pseudo-elements and state pseudo-classes: dropped before matching, so a
// rule styling `.x::before` or `.x:hover` is kept whenever `.x` is present.
// The lookahead keeps `:focus` from eating the start of `:focus-visible`.
const UNMATCHABLE_PSEUDO_RE =
  /::?(?:before|after|marker|placeholder|selection|first-line|first-letter|backdrop|-webkit-[\w-]+|-moz-[\w-]+)(?![\w-])|:(?:hover|focus-visible|focus-within|focus|active|visited)(?![\w-])/g;

export type SlideCaptureCss = {
  css: string;
  keptRules: number;
  totalRules: number;
  unreadableSheets: string[];
  fontImports: string[];
};

/**
 * A cross-origin font stylesheet can't be read, only re-imported. Google Fonts
 * URLs keep only the slide's families (Safari pays per face); null if none.
 */
export function narrowFontStylesheetUrl(
  href: string,
  families: Set<string>,
): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.hostname !== "fonts.googleapis.com") return href;
  const used = url.searchParams
    .getAll("family")
    .filter((param) =>
      families.has(param.split(":")[0].replace(/\+/g, " ").toLowerCase()),
    );
  if (used.length === 0) return null;
  url.searchParams.delete("family");
  used.forEach((param) => url.searchParams.append("family", param));
  return url.toString();
}

// `.reveal` itself and everything above it — querySelector covers only what
// is inside.
function ancestorsOf(revealEl: Element): Element[] {
  const ancestors: Element[] = [];
  for (let el: Element | null = revealEl; el; el = el.parentElement) {
    ancestors.push(el);
  }
  return ancestors;
}

function usedFontFamilies(elements: Element[]): Set<string> {
  const families = new Set<string>();
  elements.forEach((el) =>
    getComputedStyle(el)
      .fontFamily.split(",")
      .forEach((family) =>
        families.add(family.trim().replace(/["']/g, "").toLowerCase()),
      ),
  );
  return families;
}

// What stripping can leave behind: `:not(:hover)` becomes an invalid `:not()`.
const EMPTY_PSEUDO_FUNCTION_RE = /:[\w-]+\(\s*\)/g;

/** A selector list split on its top-level commas; `:is(.a, .b)` stays whole. */
function splitSelectorList(selectorText: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < selectorText.length; i++) {
    const char = selectorText[i];
    if (char === "(" || char === "[") depth++;
    else if (char === ")" || char === "]") depth--;
    else if (char === "," && depth === 0) {
      parts.push(selectorText.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(selectorText.slice(start));
  return parts;
}

// One native querySelector over the slide instead of a matches() call per
// element: the per-call overhead, not the matching, dominated on an iPad.
function selectorMatches(
  selectorText: string,
  ancestors: Element[],
  revealEl: Element,
): boolean {
  return splitSelectorList(selectorText).some((part) => {
    const selector =
      part
        .replace(UNMATCHABLE_PSEUDO_RE, "")
        .replace(EMPTY_PSEUDO_FUNCTION_RE, "")
        .trim() || "*";
    try {
      return (
        ancestors.some((el) => el.matches(selector)) ||
        revealEl.querySelector(selector) !== null
      );
    } catch {
      return false;
    }
  });
}

type Collector = {
  ancestors: Element[];
  revealEl: Element;
  families: Set<string>;
  counter: { total: number; kept: number };
  unreadableSheets: string[];
  fontImports: Set<string>;
};

function addUnreadableSheet(href: string | null, collector: Collector) {
  collector.unreadableSheets.push(href ?? "inline");
  if (!href) return;
  const narrowed = narrowFontStylesheetUrl(href, collector.families);
  if (narrowed) collector.fontImports.add(narrowed);
}

function collectSheet(sheet: CSSStyleSheet, collector: Collector): string {
  let rules: CSSRuleList;
  try {
    rules = sheet.cssRules;
  } catch {
    addUnreadableSheet(sheet.href, collector);
    return "";
  }
  return collectRules(rules, collector);
}

function collectRules(rules: CSSRuleList, collector: Collector): string {
  const { ancestors, revealEl, families, counter } = collector;
  let css = "";
  for (const rule of Array.from(rules)) {
    counter.total += 1;
    if (rule instanceof CSSStyleRule) {
      if (selectorMatches(rule.selectorText, ancestors, revealEl)) {
        counter.kept += 1;
        css += rule.cssText;
      }
    } else if (rule instanceof CSSFontFaceRule) {
      const family = rule.style
        .getPropertyValue("font-family")
        .replace(/["']/g, "")
        .trim()
        .toLowerCase();
      if (families.has(family)) {
        counter.kept += 1;
        css += rule.cssText;
      }
    } else if (rule instanceof CSSImportRule) {
      if (rule.styleSheet) css += collectSheet(rule.styleSheet, collector);
      else addUnreadableSheet(rule.href, collector);
    } else if (rule instanceof CSSMediaRule) {
      const inner = collectRules(rule.cssRules, collector);
      if (inner) css += `@media ${rule.conditionText}{${inner}}`;
    } else if (rule instanceof CSSSupportsRule) {
      const inner = collectRules(rule.cssRules, collector);
      if (inner) css += `@supports ${rule.conditionText}{${inner}}`;
    }
    // @keyframes, @layer statements etc.: a still frame needs none.
  }
  return css;
}

/**
 * The rules from every stylesheet of the page that match the capture path
 * (html → .reveal and everything inside it), plus @font-face rules for the
 * families those elements use.
 *
 * Input:   the live `.reveal` element
 * Output:  the CSS, with cross-origin font stylesheets as leading @imports
 */
export function collectSlideCaptureCss(revealEl: Element): SlideCaptureCss {
  const ancestors = ancestorsOf(revealEl);
  const families = usedFontFamilies([
    ...ancestors,
    ...Array.from(revealEl.querySelectorAll("*")),
  ]);
  const collector: Collector = {
    ancestors,
    revealEl,
    families,
    counter: { total: 0, kept: 0 },
    unreadableSheets: [],
    fontImports: new Set(),
  };
  let css = "";
  for (const sheet of Array.from(document.styleSheets)) {
    css += collectSheet(sheet, collector);
  }
  // @import has to come first in a stylesheet.
  const imports = Array.from(collector.fontImports)
    .map((href) => `@import url("${href}");`)
    .join("");
  return {
    css: imports + css,
    keptRules: collector.counter.kept,
    totalRules: collector.counter.total,
    unreadableSheets: collector.unreadableSheets,
    fontImports: Array.from(collector.fontImports),
  };
}

function waitAtMost(promise: Promise<unknown>, timeoutMs: number) {
  return Promise.race([
    promise,
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

/**
 * Adds the capture CSS to the clone and waits for its fonts: html2canvas
 * waits for fonts before onclone, and text laid out in a fallback overlaps.
 *
 * Input:   the cloned document, the CSS, the longest wait in ms
 * Mutates: the cloned document's <head>
 */
export async function addCaptureCssToClone(
  doc: Document,
  css: string,
  fontWaitMs: number,
): Promise<void> {
  const style = doc.createElement("style");
  const styleLoaded = new Promise<void>((resolve) => {
    style.addEventListener("load", () => resolve());
    style.addEventListener("error", () => resolve());
  });
  style.textContent = css;
  doc.head.appendChild(style);
  const fontsLoaded = styleLoaded.then(() => {
    // Lay out once, so the fonts the slide's text uses start loading.
    void doc.body.offsetHeight;
    return doc.fonts.ready;
  });
  await waitAtMost(fontsLoaded, fontWaitMs);
}
